import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Modal } from '../../components/ui/Modal.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { showToast } from '../../components/ui/Toaster.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { productionApi } from '../../services/productionApi.js';
import { formatCurrency } from '../../utils/formatters.js';

import { ConfirmWorkOrderModal } from './ConfirmWorkOrderModal.jsx';

function WorkOrderCreateModal({ open, onClose, onCreate }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ bomId: '', quantity: '1', warehouseId: '', workCenterId: '', laborHours: '0' });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const boms = useQuery({
    queryKey: ['production', 'boms', 'active'],
    queryFn: () => productionApi.listBoms({ activeOnly: true }).then((r) => r.items),
    enabled: open,
  });
  const warehouses = useQuery({
    queryKey: ['inventory', 'warehouses', 'for-wo'],
    queryFn: () => inventoryApi.listWarehouses().then((r) => r.items),
    enabled: open,
  });
  const workCenters = useQuery({
    queryKey: ['production', 'work-centers', 'for-wo'],
    queryFn: () => productionApi.listWorkCenters({ activeOnly: true }).then((r) => r.items),
    enabled: open,
  });

  // Live MRP preview once a BOM + quantity are chosen.
  const mrp = useQuery({
    queryKey: ['production', 'mrp', form.bomId, form.quantity],
    queryFn: () => productionApi.explodeMrp({ bomId: form.bomId, quantity: form.quantity }),
    enabled: open && !!form.bomId && Number(form.quantity) > 0,
  });

  const submit = (e) => {
    e.preventDefault();
    onCreate({
      bomId: form.bomId,
      quantity: form.quantity,
      warehouseId: form.warehouseId || null,
      workCenterId: form.workCenterId || null,
      laborHours: Number(form.laborHours) || 0,
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={t('production.orders.new')}>
      <form onSubmit={submit} className="form-stack">
        <div className="field">
          <label className="field-label" htmlFor="wo-bom">{t('production.orders.bom')}</label>
          <select id="wo-bom" className="select" required value={form.bomId} onChange={set('bomId')}>
            <option value="">{t('common.select')}</option>
            {(boms.data || []).map((b) => (
              <option key={b.id} value={b.id}>{b.code} - {b.name}</option>
            ))}
          </select>
        </div>
        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="wo-qty">{t('production.orders.quantity')}</label>
            <input id="wo-qty" type="number" min="0.0001" step="any" className="input" required value={form.quantity} onChange={set('quantity')} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="wo-wh">{t('production.orders.warehouse')}</label>
            <select id="wo-wh" className="select" required value={form.warehouseId} onChange={set('warehouseId')}>
              <option value="">{t('common.select')}</option>
              {(warehouses.data || []).map((w) => (
                <option key={w.id} value={w.id}>{w.code} - {w.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="wo-wc">{t('production.orders.workCenter')}</label>
            <select id="wo-wc" className="select" value={form.workCenterId} onChange={set('workCenterId')}>
              <option value="">{t('common.none')}</option>
              {(workCenters.data || []).map((wc) => (
                <option key={wc.id} value={wc.id}>{wc.name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="wo-hours">{t('production.orders.laborHours')}</label>
            <input id="wo-hours" type="number" min="0" step="0.01" className="input" value={form.laborHours} onChange={set('laborHours')} />
          </div>
        </div>

        {mrp.data?.requirements?.length > 0 && (
          <div className="field">
            <label className="field-label">{t('production.orders.materialPreview')}</label>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('production.boms.component')}</th>
                  <th className="num">{t('production.orders.required')}</th>
                </tr>
              </thead>
              <tbody>
                {mrp.data.requirements.map((r) => (
                  <tr key={r.componentProductId}>
                    <td>{r.componentName || r.componentSku}</td>
                    <td className="num">{r.requiredQuantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="page-actions">
          <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn primary">{t('common.create')}</button>
        </div>
      </form>
    </Modal>
  );
}

export function WorkOrdersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmFor, setConfirmFor] = useState(null);

  const workOrders = useQuery({
    queryKey: ['production', 'work-orders'],
    queryFn: () => productionApi.listWorkOrders({ limit: 200 }).then((r) => r.items),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['production', 'work-orders'] });

  const createMutation = useMutation({
    mutationFn: (body) => productionApi.createWorkOrder(body),
    onSuccess: () => { invalidate(); setModalOpen(false); },
    onError: (err) => showToast(err.message || t('common.error')),
  });
  const releaseMutation = useMutation({
    mutationFn: (id) => productionApi.setWorkOrderStatus(id, 'RELEASED'),
    onSuccess: invalidate,
    onError: (err) => showToast(err.message || t('common.error')),
  });
  const startMutation = useMutation({
    mutationFn: (id) => productionApi.setWorkOrderStatus(id, 'IN_PROGRESS'),
    onSuccess: invalidate,
    onError: (err) => showToast(err.message || t('common.error')),
  });
  const cancelMutation = useMutation({
    mutationFn: (id) => productionApi.setWorkOrderStatus(id, 'CANCELLED'),
    onSuccess: invalidate,
    onError: (err) => showToast(err.message || t('common.error')),
  });
  const completeMutation = useMutation({
    mutationFn: (id) => productionApi.completeWorkOrder(id),
    onSuccess: () => { invalidate(); showToast(t('production.orders.completedToast')); },
    onError: (err) => showToast(err.message || t('common.error')),
  });

  const rows = workOrders.data || [];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('production.orders.title')}</h1>
          <p className="muted">{t('production.orders.subtitle')}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn primary" onClick={() => setModalOpen(true)}>
            {t('production.orders.new')}
          </button>
        </div>
      </header>

      {workOrders.isLoading ? (
        <Skeleton variant="rows" count={6} />
      ) : rows.length === 0 ? (
        <EmptyState message={t('production.orders.empty')} />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('production.orders.number')}</th>
              <th>{t('production.boms.product')}</th>
              <th className="num">{t('production.orders.quantity')}</th>
              <th>{t('common.status')}</th>
              <th className="num">{t('production.orders.totalCost')}</th>
              <th className="num">{t('production.orders.unitCost')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((wo) => (
              <tr key={wo.id}>
                <td className="mono"><Link to={`/production/work-orders/${wo.id}`}>{wo.orderNumber}</Link></td>
                <td>{wo.productName || wo.productSku || '-'}</td>
                <td className="num">{Number(wo.confirmedQuantity) > 0 ? `${wo.confirmedQuantity} / ${wo.quantity}` : wo.quantity}</td>
                <td><span className={`status-pill status-${wo.status.toLowerCase()}`}>{t(`production.status.${wo.status}`)}</span></td>
                <td className="num">{Number(wo.totalCost) > 0 ? formatCurrency(wo.totalCost) : '-'}</td>
                <td className="num">{Number(wo.unitCost) > 0 ? formatCurrency(wo.unitCost) : '-'}</td>
                <td>
                  <div className="row-actions">
                    {wo.status === 'DRAFT' && (
                      <button type="button" className="btn" onClick={() => releaseMutation.mutate(wo.id)}>
                        {t('production.orders.release')}
                      </button>
                    )}
                    {wo.status === 'RELEASED' && (
                      <button type="button" className="btn" onClick={() => startMutation.mutate(wo.id)}>
                        {t('production.orders.start')}
                      </button>
                    )}
                    {(wo.status === 'RELEASED' || wo.status === 'IN_PROGRESS') && (
                      <button type="button" className="btn" onClick={() => setConfirmFor(wo)}>
                        {t('pc2.action.button')}
                      </button>
                    )}
                    {(wo.status === 'RELEASED' || wo.status === 'IN_PROGRESS') && Number(wo.confirmedQuantity) === 0 && (
                      <button type="button" className="btn primary" onClick={() => completeMutation.mutate(wo.id)}>
                        {t('production.orders.complete')}
                      </button>
                    )}
                    {(wo.status === 'DRAFT' || wo.status === 'RELEASED' || wo.status === 'IN_PROGRESS') && (
                      <button type="button" className="btn" onClick={() => cancelMutation.mutate(wo.id)}>
                        {t('production.orders.cancel')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <WorkOrderCreateModal open={modalOpen} onClose={() => setModalOpen(false)} onCreate={(body) => createMutation.mutate(body)} />
      <ConfirmWorkOrderModal workOrder={confirmFor} onClose={() => setConfirmFor(null)} />
    </section>
  );
}
